import React, { useState, useEffect, useRef } from "react";
import SectionHeader from "@src/components/widgets/SectionHeader";
import SpinningLoader from "@src/components/common/SpinningLoader";
import OrderStatuses, {
  OrderStatus,
  OrderStatusLabels,
} from "@src/constants/OrderStatuses";
import Distributors from "@src/constants/Distributors";
import OrderDetailTile, {
  Order,
} from "@src/components/widgets/OrderDetailTile/OrderDetailTile";
import BackorderedProductsTile from "@src/components/widgets/BackorderedProductsTile";
import {
  Checkbox,
  Input,
  PaginationControls,
  useToast,
} from "@swordsweeper/ssi-ui-component-library";
import map from "lodash/map";
import filter from "lodash/filter";
import debounce from "lodash/debounce";
import isEmpty from "lodash/isEmpty";
import pick from "lodash/pick";
import * as OrderApi from "@src/api/OrderApi";
import DateSelect from "@src/components/common/DateSelect/DateSelect";
import moment from "moment";
import DistributorLogo from "@src/components/common/DistributorLogo";
import {
  PaginationFilterChangeEvent,
  PaginationValues,
} from "@src/types/pagination";
import { useCurrentUserStore } from "@src/state/currentUserState";
import { DistributorConnectionStatuses } from "@src/components/widgets/AccountDistributorSettings/DistributorConnectionConstants";

const ORDER_DATE_PAST_MONTH = "past_month";
const ORDER_DATE_PAST_3_MONTHS = "past_3_months";
const ORDER_DATE_PAST_6_MONTHS = "past_6_months";
const ORDER_DATE_PAST_YEAR = "past_year";
const ORDER_DATE_LAST_YEAR = "last_year";
const ORDER_DATE_RANGE = "date_range";

interface Filters {
  orderStatus: Record<string, boolean>;
  orderDate: Record<string, boolean>;
  dateRange: Record<string, Date>;
  distributor: Record<string, boolean>;
}

interface FilterChangeEvent {
  name: keyof Filters;
  key: string;
  value: boolean;
}

function OrdersPage() {
  const { showToast } = useToast();
  const { getDistributorConnection, distributorConnections } =
    useCurrentUserStore();
  const [isLoadingOrders, setIsLoadingOrders] = useState<boolean>(false);
  const [orders, setOrders] = useState<Order[] | null>(null);
  const [paginationFilters, setPaginationFilters] = useState<PaginationValues>({
    pageSize: 20,
    page: 1,
    totalPages: 0,
    totalCount: 0,
  });
  const [orderResponseMetaData, setOrderResponseMetaData] = useState<{
    statusCounts: Record<string, number>;
  } | null>(null);
  const [search, setSearch] = useState<string | null>(null);
  const [filters, setFilters] = useState<Filters>({
    orderStatus: {},
    orderDate: {},
    dateRange: {},
    distributor: {},
  });
  const scrollableOrdersContainerRef = useRef<HTMLDivElement>(null);
  const isInitalRenderRef = useRef<boolean>(true);
  const orderIds = orders?.map(({ id }) => id);

  useEffect(() => {
    const safePaginationFilters = { ...paginationFilters };
    safePaginationFilters.page = 1;
    debouncedHandleLoadOrders(search, safePaginationFilters, filters);
  }, [search, paginationFilters.pageSize, JSON.stringify(filters)]);

  useEffect(() => {
    debouncedHandleLoadOrders(search, paginationFilters, filters);
  }, [paginationFilters.page]);

  useEffect(() => {
    if (orders && scrollableOrdersContainerRef.current) {
      requestAnimationFrame(() => {
        if (scrollableOrdersContainerRef.current) {
          scrollableOrdersContainerRef.current.scrollTo({
            top: 0,
            behavior: isInitalRenderRef.current ? "auto" : "smooth",
          });
          isInitalRenderRef.current = false;
        }
      });
    }
  }, [orderIds?.join(",")]);

  const debouncedHandleLoadOrders = useRef(
    debounce(
      async (
        search: string | null,
        paginationFilters: PaginationValues,
        filters: Filters,
      ) => {
        setIsLoadingOrders(true);
        try {
          const {
            orderStatus,
            orderDate,
            distributor,
            dateRange,
            ...remainingFilters
          } = filters;
          const orderStatusFilter = Object.keys(orderStatus)
            .filter((key) => orderStatus[key])
            .join(",");
          const distributorFilter = isEmpty(distributor)
            ? null
            : Object.keys(distributor)
                .filter((key) => distributor[key])
                .join(",");

          const orderDateFilter = Object.keys(orderDate).find(
            (key) => orderDate[key] === true,
          );

          const combinedFilters = {
            ...pick(paginationFilters, ["page", "pageSize"]),
            ...remainingFilters,
            orderDate: orderDateFilter || null,
            orderStatus: orderStatusFilter || null,
            distributor: distributorFilter || null,
            dateFrom: !!dateRange.dateFrom
              ? moment(dateRange.dateFrom).format("YYYY-MM-DD")
              : null,
            dateTo: !!dateRange.dateTo
              ? moment(dateRange.dateTo).format("YYYY-MM-DD")
              : null,
            search,
          };

          const response = await OrderApi.getOrders(combinedFilters);

          const { results, meta, ...updatedPaginationFilters } = response.body;
          setOrders(results);
          setOrderResponseMetaData(meta);
          setPaginationFilters({
            ...paginationFilters,
            ...updatedPaginationFilters,
          });
        } catch (e) {
          console.error(e);
          showToast({
            message: "Error loading orders",
            variant: "error",
          });
        } finally {
          setIsLoadingOrders(false);
        }
      },
      500,
    ),
  ).current;

  const handleFilterChanged = ({
    name,
    key,
    value,
  }: FilterChangeEvent): void => {
    const updatedFilters = { ...filters };

    if (name === "orderDate") {
      // Only 1 can be selected
      updatedFilters[name] = { [key]: value };
      updatedFilters.dateRange = {};
    } else {
      updatedFilters[name][key] = value;
    }
    setFilters(updatedFilters);
  };

  const handlePaginationFilterChanged = ({
    name,
    value,
  }: PaginationFilterChangeEvent): void => {
    const updatedPaginationFilters = {
      ...paginationFilters,
      [name]: value,
    };
    setPaginationFilters(updatedPaginationFilters);
  };

  const renderFilterCheckbox = (
    name: keyof Filters,
    key: string,
    label: string | React.ReactNode,
  ): React.ReactElement => {
    const isChecked = filters[name][key];
    return (
      <div id={`order-filter-checkbox-${name}-${key}`}>
        <Checkbox
          data-testid={`order-filter-checkbox-${name}-${key}-testid`}
          checked={isChecked}
          value={isChecked}
          label={<span className="text-sm padding-left-sm">{label}</span>}
          name={name}
          onChange={(value: boolean) =>
            handleFilterChanged({ name, key, value })
          }
        />
      </div>
    );
  };

  const STATUS_ORDER: OrderStatus[] = [
    OrderStatuses.Pending,
    OrderStatuses.Shipped,
    OrderStatuses.Delivered,
    OrderStatuses.Returned,
    OrderStatuses.Canceled,
  ];

  const isNoOrdersSectionOnCurrentPage = (
    orderStatus: OrderStatus,
  ): boolean => {
    const statusCounts = orderResponseMetaData?.statusCounts || {};
    // Virtual position = sum of counts for all statuses ordered before this one
    let virtualPosition = 0;
    for (const status of STATUS_ORDER) {
      if (status === orderStatus) break;
      virtualPosition += statusCounts[status] || 0;
    }
    const pageStart = (paginationFilters.page - 1) * paginationFilters.pageSize;
    const pageEnd = paginationFilters.page * paginationFilters.pageSize - 1;
    return virtualPosition >= pageStart && virtualPosition <= pageEnd;
  };

  const renderOrderSection = (orderStatus: OrderStatus) => {
    const ordersForSection = !!orders
      ? filter(orders, { status: orderStatus })
      : null;
    if (ordersForSection && ordersForSection.length > 0) {
      return (
        <div data-testid={`orders-section-${orderStatus}`}>
          <div className="font-weight-bold padding-bottom-md">
            {OrderStatusLabels[orderStatus]} Orders
          </div>
          <div className="flex-column spaced-content-v-lg">
            {map(ordersForSection, (order) => {
              return (
                <React.Fragment key={`order-${order.id}`}>
                  <OrderDetailTile order={order} />

                  {order.backorderedProducts.length > 0 && (
                    <BackorderedProductsTile
                      backorderedProducts={order.backorderedProducts}
                      includeDistributorImage
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      );
    }

    // Only show "No X Orders" if this section's position falls on the current page
    if (
      (orderResponseMetaData?.statusCounts[orderStatus] || 0) === 0 &&
      (Object.keys(filters.orderStatus).length === 0 ||
        filters.orderStatus[orderStatus]) &&
      isNoOrdersSectionOnCurrentPage(orderStatus)
    ) {
      return (
        <div
          className="text-sm text-light text-italic"
          data-testid={`orders-section-${orderStatus}-no-results`}
        >
          No {OrderStatusLabels[orderStatus]} Orders
        </div>
      );
    }

    return null;
  };

  return (
    <>
      {isLoadingOrders && <SpinningLoader />}

      <div className="spaced-content-v-xl flex-1 min-height-0 flex-column">
        <div className="flex-1 min-height-0 flex-column">
          <SectionHeader header={<span className="text-xl">Orders</span>} />

          <div className="flex-row spaced-content-lg flex-1 min-height-0">
            <div className="flex-none flex-column text-sm spaced-content-v-lg">
              <div className="padding-bottom-sm">
                <Input
                  id="order-list-page-search-input"
                  data-testid="order-list-page-search-input"
                  defaultValue=""
                  onChange={setSearch}
                  value={search}
                  width={213}
                  height={28}
                  fontSize={12}
                  placeholder="Search by order number"
                  icon={<i className="fa-light fa-magnifying-glass" />}
                />
              </div>

              <div>
                <div className="font-weight-bold padding-bottom-sm">
                  Order Status
                </div>
                <div className="flex-column spaced-content-v-xs">
                  {map(OrderStatuses, (value: string, key: string) => (
                    <React.Fragment key={`order-status-filter-${key}`}>
                      {renderFilterCheckbox("orderStatus", value, key)}
                    </React.Fragment>
                  ))}
                </div>
              </div>

              <div>
                <div className="font-weight-bold padding-bottom-sm">
                  Order Date
                </div>
                <div className="flex-column spaced-content-v-xs">
                  {renderFilterCheckbox(
                    "orderDate",
                    ORDER_DATE_PAST_MONTH,
                    "Past Month",
                  )}
                  {renderFilterCheckbox(
                    "orderDate",
                    ORDER_DATE_PAST_3_MONTHS,
                    "Past 3 Months",
                  )}
                  {renderFilterCheckbox(
                    "orderDate",
                    ORDER_DATE_PAST_6_MONTHS,
                    "Past 6 Months",
                  )}
                  {renderFilterCheckbox(
                    "orderDate",
                    ORDER_DATE_PAST_YEAR,
                    "Past Year",
                  )}
                  {renderFilterCheckbox(
                    "orderDate",
                    ORDER_DATE_LAST_YEAR,
                    "Last Year",
                  )}
                  {renderFilterCheckbox(
                    "orderDate",
                    ORDER_DATE_RANGE,
                    "Date Range",
                  )}

                  {filters.orderDate[ORDER_DATE_RANGE] && (
                    <div className="flex-column spaced-content-v-xs margin-top-sm">
                      <div className="flex-column">
                        <label>From</label>
                        <DateSelect
                          name="dateRange"
                          value={filters.dateRange.dateFrom}
                          onChange={(change) =>
                            handleFilterChanged({ ...change, key: "dateFrom" })
                          }
                          maxDate={filters.dateRange.dateTo}
                        />
                      </div>
                      <div className="flex-column">
                        <label>To</label>
                        <DateSelect
                          name="dateRange"
                          value={filters.dateRange.dateTo}
                          onChange={(change) =>
                            handleFilterChanged({ ...change, key: "dateTo" })
                          }
                          minDate={filters.dateRange.dateFrom}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <div className="font-weight-bold padding-bottom-sm">
                  Distributor
                </div>
                <div className="flex-column spaced-content-v-xs">
                  {map(Distributors, (value: string, key: string) => {
                    // Filter out NOT_CONNECTED distributors
                    // Only filter if distributorConnections have been loaded (not null)
                    if (distributorConnections !== null) {
                      const connection = getDistributorConnection(value);
                      // Filter out if no connection record (NOT_CONNECTED) or status is explicitly NOT_CONNECTED
                      if (
                        !connection ||
                        connection.status ===
                          DistributorConnectionStatuses.NotConnected
                      ) {
                        return null;
                      }
                    }

                    const distributorLogo = (
                      <DistributorLogo distributorSlug={value} height={20} />
                    );
                    return (
                      <React.Fragment key={`distributor-filter-${key}`}>
                        {renderFilterCheckbox(
                          "distributor",
                          value,
                          distributorLogo,
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="flex-1 flex-column min-height-0">
              <div
                ref={scrollableOrdersContainerRef}
                className="flex-1 flex-column spaced-content-v-lg overflow-y-scroll"
              >
                {!!orders && (
                  <>
                    {renderOrderSection(OrderStatuses.Pending)}
                    {renderOrderSection(OrderStatuses.Shipped)}
                    {renderOrderSection(OrderStatuses.Delivered)}
                    {renderOrderSection(OrderStatuses.Returned)}
                    {renderOrderSection(OrderStatuses.Canceled)}
                  </>
                )}
              </div>
              <div className="flex-none padding-top-lg">
                <PaginationControls
                  onChange={handlePaginationFilterChanged}
                  values={paginationFilters}
                  paginationTextValue="Orders per page:"
                  itemsLabel="orders"
                  data-testid="orders-page-pagination-controls"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default OrdersPage;
